import Grid from '@mui/material/Grid2';
import Panel from 'Components/Panel';
import SearchInput from 'Components/SearchInput';
import Table from 'Components/SimpleTable';
import { sortBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectVerificationProfile } from 'selectors/devices';
import { selectReadingsGroupedByVerification } from 'selectors/deviceVerificationReadings';
import { renderChecks, renderComments, renderStatusBadge, renderYesNo } from './renderUtils';
import { checksNeeded, getTechnicianComments } from './utils';

const getLastVerification = (deviceId, verificationIds, verificationModels) => {
    let last;
    verificationIds.forEach(id => {
        const verification = verificationModels[id];
        if (verification.device_id === deviceId && (!last || verification.created_at >= last.created_at)) {
            last = verification;
        }
    });
    return last;
};

// Every row is a different device, and so possibly a different kind of verification: each cell asks
// for its own profile, the same way a single device's screen does.
const TechChecks = ({ count, deviceId }) => {
    const profile = useSelector(useMemo(() => selectVerificationProfile(deviceId), [deviceId]));
    return renderChecks(count, checksNeeded(profile?.comparison));
};

const StatusTable = ({ devices, showWiseBox = false }) => {
    const navigate = useNavigate();
    const allDeviceModels = useSelector(state => state.devices.models);
    const allVerificationModels = useSelector(state => state.deviceVerifications.models);
    const allVerificationIds = useSelector(state => state.deviceVerifications.ids);
    const readingsByVerification = useSelector(selectReadingsGroupedByVerification);
    const [search, setSearch] = useState('');

    const columns = [
        ...(showWiseBox ? [{ label: 'WiseBox', dataKey: 'wiseBoxName' }] : []),
        { label: 'Device Name', dataKey: 'name' },
        { label: 'Installation Zone', dataKey: 'installationZone' },
        { label: 'Equipment Name', dataKey: 'equipmentName' },
        { label: 'Protocol', dataKey: 'in_protocol_name' },
        { label: 'Final Status', dataKey: 'finalStatus', transform: row => renderStatusBadge(row.finalStatus) },
        { label: 'Tech Checks?', dataKey: 'techChecks', transform: row => <TechChecks count={row.techChecks} deviceId={row.id}/> },
        { label: 'Live?', dataKey: 'online', transform: row => renderYesNo(row.online) },
        {
            label: 'Comments',
            dataKey: 'comments',
            transform: row => renderComments(row.technicianComments, row.finalComments)
        }
    ];

    const rows = useMemo(() => {
        return devices.map(device => {
            const rootDevice = allDeviceModels[device.root_device_id];
            const verification = getLastVerification(device.id, allVerificationIds, allVerificationModels) || {};
            const readings = readingsByVerification[verification.id] || [];
            const lastReading = readings.at(-1);

            return {
                id: device.id,
                wiseBoxName: rootDevice ? rootDevice.name : '',
                installationZone: device.installation_zone_full_path || '',
                equipmentName: device.equipment_name || '',
                name: device.name,
                in_protocol_name: device.in_protocol_name,
                online: lastReading?.online || false,
                technicianComments: getTechnicianComments(readingsByVerification[verification.id]),
                techChecks: readings.length,
                finalComments: verification.final_comments || '',
                finalStatus: verification.final_status === 'no_status' ? '' : (verification.final_status || ''),
                finalValidated: verification.state === 'ok' || verification.state === 'not_ok'
            };
        });
    }, [allDeviceModels, allVerificationIds, allVerificationModels, devices, readingsByVerification]);

    const filteredRows = useMemo(() => {
        if (!search) {
            return sortBy(rows, 'name');
        }
        const term = search.toLowerCase();
        return sortBy(rows.filter(row => {
            const values = [
                row.wiseBoxName, row.installationZone, row.equipmentName,
                row.name, row.in_protocol_name, row.technicianComments,
                row.finalStatus, row.finalComments,
                row.finalValidated ? 'validated' : 'pending'
            ];
            return values.some(v => v && v.toLowerCase().includes(term));
        }), 'name');
    }, [rows, search]);

    const header = (
        <Grid alignItems="center" container>
            <Grid size={2}/>
            <Grid size={8}>
                <SearchInput
                    onSearchChange={setSearch}
                    searchValue={search}
                />
            </Grid>
            <Grid size={2}/>
        </Grid>
    );

    return (
        <Panel header={header}>
            <Table
                columns={columns}
                disableScrollbar
                onRowClick={id => navigate(`/devices/${id}/verifications`, { state: { openVerificationDrawer: true } })}
                rows={filteredRows}
                rowsPerPage={10000}
            />
        </Panel>
    );
};

export default StatusTable;
